import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  applyTypography,
  BaseButton,
  JOB_CARD_TYPE,
  TITLE_CHIP_MAP,
  useAuth,
  useIsMobile,
  useLoginRequiredModal,
} from '@repo/common';
import CardOptionList from './Admin/CardOptionList';
import JobCompanyLogo from './JobCompanyLogo';

import { ReactComponent as BookmarkIconOutline } from '@repo/common/assets/icons/32px/icon_32px_bookmark_outline.svg';
import { ReactComponent as BookmarkIconFilled } from '@repo/common/assets/icons/32px/icon_32px_bookmark_filled.svg';
import { ReactComponent as HiddenIcon } from '@repo/common/assets/icons/32px/icon_32px_hidden.svg';
import { ReactComponent as OptionIcon } from '@repo/common/assets/icons/20px/icon_20px_option_filled.svg';
import { ReactComponent as RestoreIcon } from '@repo/common/assets/icons/32px/icon_32px_undo.svg';
import { ReactComponent as MobileRestoreIcon } from '@repo/common/assets/icons/20px/icon_20px_undo-left.svg';

const JobCard = ({
  job,
  isAdmin = false,
  isSaved = false,
  isAllHiddenJobPage = false,
  onSaveToast,
  onToggleBookmark,
  onToggleHidden,
  className = '',
  $isMobile,
}) => {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const { showLoginModal } = useLoginRequiredModal();

  const [saved, setSaved] = useState(Boolean(job?.isBookmarked ?? isSaved));
  const [hidden, setHidden] = useState(Boolean(job?.isHiddenByUser));
  const [showOptions, setShowOptions] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);

  const autoIsMobile = useIsMobile();
  /* 카드마다 레이아웃이 통째로 달라 CSS 분기 대신 JS 분기(액션 버튼/드롭다운 중복 마운트 방지) */
  const isMobileLayout = $isMobile ?? autoIsMobile;

  const {
    id,
    title,
    company,
    companyLogoUrl,
    location,
    hasE7Support,
    employmentType,
    keywords,
    titleChips,
    status,
  } = job;

  const isPostHiddenByAdmin = status === JOB_CARD_TYPE.HIDDEN;
  const isClosed = status === JOB_CARD_TYPE.CLOSED;
  const showHiddenState = hidden || isPostHiddenByAdmin;

  const isUnavailable =
    status !== JOB_CARD_TYPE.ACTIVE && status !== JOB_CARD_TYPE.DRAFT;
  const displayKeywords = (keywords || []).filter(
    (keyword) => keyword && keyword !== employmentType,
  );

  useEffect(() => {
    setSaved(Boolean(job?.isBookmarked ?? isSaved));
    setHidden(Boolean(job?.isHiddenByUser));
  }, [job?.id, job?.isBookmarked, job?.isHiddenByUser, isSaved]);

  const handleSave = async () => {
    if (!isLoggedIn) {
      showLoginModal('jobboard_default_save_cta');
      return;
    }

    const next = !saved;
    setSaved(next);

    try {
      await onToggleBookmark?.(id, next);
      if (next) {
        onSaveToast?.();
      }
    } catch (error) {
      console.error('Bookmark toggle failed:', error);
      setSaved(!next);
    }
  };

  const handleHide = async (nextValue = !hidden) => {
    if (!isLoggedIn) {
      showLoginModal('jobboard_default_hide_cta');
      return;
    }

    setHidden(nextValue);
    try {
      await onToggleHidden?.(id, nextValue);
    } catch (error) {
      console.error('Hidden toggle failed:', error);
      setHidden(!nextValue);
    }
  };

  /* 타이틀 칩 (Quick Response / Urgently Hiring) — 없으면 행 자체를 렌더하지 않는다 */
  const visibleTitleChips = (titleChips || []).filter(
    (chip) => TITLE_CHIP_MAP[chip],
  );
  const hasTitleChips = visibleTitleChips.length > 0;
  const titleChipNodes = visibleTitleChips.map((chip) => {
    const meta = TITLE_CHIP_MAP[chip];

    return (
      <BaseButton
        key={chip}
        type='text'
        size='s'
        icon={meta.icon}
        gap='gap-0.5'
        bgclass='h-6 bg-purple-purple01 py-1 pl-1 pr-[6px]'
        textclass='text-purple-purple04 typo-b12'
      >
        {meta.label}
      </BaseButton>
    );
  });

  const actionButtons = (
    <div
      className='relative flex flex-col items-center gap-2 shrink-0'
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {isAdmin ? (
        <>
          <button
            type='button'
            className='flex items-center justify-center text-icon-placeholder hover:text-icon-primary p-[10px]'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              setAnchorRect(rect);
              setShowOptions(true);
            }}
          >
            <OptionIcon />
          </button>

          {showOptions && (
            <CardOptionList
              anchorRect={anchorRect}
              onClose={() => setShowOptions(false)}
            />
          )}
        </>
      ) : isAllHiddenJobPage ? (
        <div
          className='flex flex-col items-center justify-center tabletH:px-2'
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleHide(false);
          }}
        >
          {isMobileLayout ? <MobileRestoreIcon /> : <RestoreIcon />}
          <div className='typo-b11 text-text-primary'>Restore</div>
        </div>
      ) : (
        <>
          <button
            type='button'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSave();
            }}
            className='flex items-center justify-center transition-transform duration-150 size-6 tabletH:size-8 text-icon-primary active:scale-95'
          >
            {saved ? (
              <BookmarkIconFilled className='w-6 h-6 tabletH:w-7 tabletH:h-7' />
            ) : (
              <BookmarkIconOutline className='w-6 h-6 tabletH:w-7 tabletH:h-7' />
            )}
          </button>
          {isLoggedIn && (
            <button
              type='button'
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleHide();
              }}
              className='transition-transform duration-150 w-6 h-6 tabletH:w-7 tabletH:h-7 text-icon-placeholder active:scale-95'
            >
              <HiddenIcon className='w-6 h-6 tabletH:w-7 tabletH:h-7 text-icon-placeholder' />
            </button>
          )}
        </>
      )}
    </div>
  );

  return (
    <div
      className={`group relative
        ${
          isClosed
            ? 'bg-grayscale-gray02 border-grayscale-gray05'
            : showHiddenState && !isAllHiddenJobPage
            ? 'bg-grayscale-gray02 border-grayscale-gray03'
            : 'bg-grayscale-gray00 border-grayscale-gray02 hover:border-surface-green40 active:border-surface-green40 active:bg-green-light01'
        }
        ${isClosed ? 'cursor-not-allowed' : 'cursor-pointer'}
        border-[1.6px]
        transition-all duration-200 ease-out
        border-solid flex flex-col gap-3 tabletH:gap-8 items-start min-h-[115px] tabletH:min-h-[180px]
        overflow-clip px-6 py-[18px] tabletH:p-6 rounded-[20px] tabletH:rounded-[30px] w-full ${className}`}
      onClick={() => {
        if (!isAllHiddenJobPage && (showHiddenState || isClosed)) return;

        if (isUnavailable) {
          navigate(`/jobs/${id}/unavailable`);
        } else {
          navigate(`/jobs/${id}`);
        }
      }}
    >
      {showHiddenState && !isAllHiddenJobPage ? (
        <div className='flex items-start w-full gap-2'>
          <HiddenIcon className='w-6 h-6 tabletH:w-7 tabletH:h-7 text-icon-success shrink-0' />
          <div className='flex flex-col items-start gap-3 tabletH:gap-4'>
            <div className='flex flex-col items-start gap-0.5 mt-0.5'>
              <div className='typo-b16Strong tabletH:typo-title3 text-text-success'>
                Job hidden
              </div>
              <div className='typo-b12Strong text-text-success'>
                This job won’t appear again in your feed.
              </div>
            </div>
            <div className='flex flex-wrap items-start content-start gap-2'>
              <BaseButton
                type={isMobileLayout ? 'text_basic' : 'text'}
                size={isMobileLayout ? 's' : 'm'}
                hideIcon
                onClick={(e) => {
                  e.stopPropagation();
                  handleHide(false);
                }}
              >
                Undo
              </BaseButton>
              <BaseButton
                type={isMobileLayout ? 'text_basic' : 'text'}
                size={isMobileLayout ? 's' : 'm'}
                hideIcon
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/jobs/hidden/all');
                }}
              >
                View hidden jobs
              </BaseButton>
            </div>
          </div>
        </div>
      ) : (
        <>
          {isMobileLayout ? (
            <div className='relative flex w-full flex-col gap-2 cursor-pointer'>
              <div className='absolute right-0 top-0 z-10'>{actionButtons}</div>

              {hasTitleChips && (
                <div className='flex flex-wrap items-start gap-1.5 pr-9'>
                  {titleChipNodes}
                </div>
              )}

              <p
                className={`typo-b16Strong
                  ${isClosed ? '' : 'group-active:text-green-deep03'}
                  text-text-primary transition-colors duration-200
                  w-full pr-9 line-clamp-2`}
              >
                {title}
              </p>

              <div className='relative flex w-full min-w-0 items-center gap-2'>
                {companyLogoUrl && (
                  <JobCompanyLogo
                    src={companyLogoUrl}
                    sizeClassName='size-6 shrink-0 rounded-[4px]'
                    textClassName='typo-b11 group-active:text-surface-green40'
                    backgroundClassName='group-active:bg-surface-green10'
                  />
                )}
                <p className='typo-b13 text-text-placeholder truncate'>
                  {company}
                </p>
                <div className='bg-icon-placeholder rounded shrink-0 size-[3px]' />
                <p className='typo-b13 text-text-placeholder truncate'>
                  {location}
                </p>
              </div>
            </div>
          ) : (
            <div className='flex items-center w-full gap-4 cursor-pointer'>
              <div className='flex flex-[1_0_0] gap-4 items-start min-h-px min-w-px'>
                <JobCompanyLogo
                  src={companyLogoUrl}
                  sizeClassName='size-[72px] shrink-0'
                  textClassName='typo-b14 group-active:text-surface-green40 '
                  backgroundClassName='group-active:bg-surface-green10'
                />

                <div className='flex flex-[1_0_0] flex-col gap-1.5 items-start justify-center min-h-[72px] min-w-px'>
                  {hasTitleChips && (
                    <div className='flex flex-wrap gap-1.5 items-start relative shrink-0 w-full'>
                      {titleChipNodes}
                    </div>
                  )}

                  <p
                    className={`typo-title2
                      ${isClosed ? '' : 'group-hover:text-green-deep03 group-active:text-green-deep03'}
                      text-text-primary transition-colors duration-200
                      w-full line-clamp-3 overflow-visible`}
                  >
                    {title}
                  </p>

                  <div className='relative flex items-center gap-2 w-full'>
                    <p className='typo-b14 text-text-placeholder line-clamp-3 overflow-visible'>
                      {company}
                    </p>
                    <div className='bg-icon-placeholder rounded shrink-0 size-[3px]' />
                    <p className='typo-b14 text-text-placeholder line-clamp-3 overflow-visible'>
                      {location}
                    </p>
                  </div>
                </div>
              </div>

              {actionButtons}
            </div>
          )}

          {/* Tags */}
          <div className='relative flex flex-wrap items-start w-full gap-1 tabletH:gap-2 shrink-0'>
            {hasE7Support && (
              <BaseButton
                type='text'
                hideIcon
                bgclass='px-1.5 py-[3px] tabletH:px-3 tabletH:py-[6px] bg-green-light01 group-active:bg-green-light02 border-none h-[21px] tabletH:h-7'
                textclass='text-green-deep04 typo-b11Strong tabletH:typo-b13Strong'
              >
                E-7 Support
              </BaseButton>
            )}
            <BaseButton
              type='text'
              width='w-fit'
              hideIcon
              bgclass='px-1.5 py-[3px] tabletH:px-3 tabletH:py-[6px] h-[21px] tabletH:h-7'
              textclass='typo-b11Strong tabletH:typo-b13Strong group-active:bg-surface-green10 bg-grayscale-gray01 text-text-secondary'
            >
              {employmentType}
            </BaseButton>
            {displayKeywords.map((keyword, idx) => (
              <BaseButton
                key={`${keyword}-${idx}`}
                type='text'
                width='w-fit'
                hideIcon
                bgclass='px-1.5 py-[3px] tabletH:px-3 tabletH:py-[6px] h-[21px] tabletH:h-7'
                textclass='typo-b11Strong tabletH:typo-b13Strong group-active:bg-surface-green10 bg-grayscale-gray01 text-text-secondary'
              >
                {keyword}
              </BaseButton>
            ))}
          </div>

          {isClosed && (
            <div className='absolute inset-0 z-20 flex items-center justify-center rounded-[20px] tabletH:rounded-[30px] bg-surface-gray60 pointer-events-auto'>
              <p className='typo-b14Strong tabletH:typo-b18Strong text-text-inverse'>
                This Job is closed.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default JobCard;
